import { cloneDeep, isString } from "lodash";
import { clearAuthCache, getTenantId, getToken } from "../../auth";
import signMd5Utils from "../../encryption/signMd5Utils";
import { AxiosTransform, CreateAxiosOptions } from "./types";
import { t } from "../../../i18n";
import { deepMerge, formatRequestDate, joinTimestamp, setObjToUrlParams } from "./helper";
import { ConfigEnum, ContentTypeEnum, GlobalRequestStatusEnum, RequestEnum, ResultEnum } from "../../../enums/httpEnum";
import env from "../../../configs/env";
import { VAxios } from "./Axios";
import store from "../../../store";
import dialogSilencePlugin from "../../../plugins/elementExtensions/dialogSilence.plugin";
import Vue from "vue";
import { Persistent } from "../../cache/persistent";
import { Message, MessageBox } from "element-ui";
import Router from "../../../router";

let currentGlobalRequestStatus = GlobalRequestStatusEnum.NORMAL;

function switchToLoginPage(clearLoginData = true) {
    if (clearLoginData) {
        Persistent.clearAll(true);
    }
    window.location.href = Router.resolve({ name: "login" }).href;
}

let hasGlobalMessageBoxSilent = false;
function setGlobalMessageBoxSilent() {
    if (!hasGlobalMessageBoxSilent) {
        Vue.use(dialogSilencePlugin);
        hasGlobalMessageBoxSilent = true;
    }
}

let hasPagePaint = false;
let isWaitingUserToChoose = false;
async function showUserChooseBox() {
    if (!hasPagePaint) {
        switchToLoginPage();
        return;
    }

    if (isWaitingUserToChoose) return;
    isWaitingUserToChoose = true;
    try {
        await MessageBox.confirm("账号已在其他地方登录，是否返回到登录页面重新登录？", "系统提示", {
            confirmButtonText: "重新登录",
            cancelButtonText: "留在当前页面",
            cancelButtonClass: "is-plain",
        });
    } finally {
        isWaitingUserToChoose = false;
    }
}

async function dealApiResponseError(status, msg, errorMessageMode = "none") {
    let errMessage = "";

    switch (status) {
        case 400:
            errMessage = `${msg}`;
            break;
        // 401: Not logged in
        // Jump to the login page if not logged in, and carry the path of the current page
        // Return to the current page after successful login. This step needs to be operated on the login page.
        case 401:
            errMessage = msg || t("sys.api.errMsg401");
            switchToLoginPage(true);
            break;
        case 403:
            errMessage = t("sys.api.errMsg403");
            break;
        // 404请求不存在
        case 404:
            errMessage = t("sys.api.errMsg404");
            break;
        case 405:
            errMessage = t("sys.api.errMsg405");
            break;
        case 408:
            errMessage = t("sys.api.errMsg408");
            break;
        case 500:
            errMessage = t("sys.api.errMsg500");
            break;
        case 501:
            errMessage = t("sys.api.errMsg501");
            break;
        case 502:
            errMessage = t("sys.api.errMsg502");
            break;
        case 503:
            errMessage = t("sys.api.errMsg503");
            break;
        case 504:
            errMessage = t("sys.api.errMsg504");
            break;
        case 505:
            errMessage = t("sys.api.errMsg505");
            break;
        default:
    }

    if (errMessage) {
        if (errorMessageMode === "modal") {
            MessageBox.confirm(errMessage, t("sys.api.errorTip"));
        } else if (errorMessageMode === "message") {
            MessageBox.alert(errMessage, t("sys.api.errorTip"));
        }
    }
    return errMessage;
}

/**
 * @description: 数据处理，方便区分多种处理方式
 * @type {AxiosTransform}
 */
const transform = {
    transformRequestHook: (res, options) => {
        const { isTransformResponse, isReturnNativeResponse } = options;
        // 是否返回原生响应头 比如：需要获取响应头时使用该属性
        if (isReturnNativeResponse) {
            return res;
        }
        // 不进行任何处理，直接返回
        // 用于页面代码可能需要直接获取code，data，message这些信息时开启
        if (!isTransformResponse) {
            return res.data;
        }

        let { data } = res;
        if (!data) {
            // return '[HTTP] Request has no return value';
            throw new Error(t("sys.api.apiRequestFailed"));
        }
        //  这里 code，result，message为 后台统一的字段，需要在 types.ts内修改为项目自己的接口返回格式
        const { code, message, result, success } = data;
        // 这里逻辑可以根据项目进行修改
        if (success) return result;
        let timeoutMsg = "";
        switch (code) {
            case ResultEnum.TIMEOUT:
                timeoutMsg = t("sys.api.timeoutMessage");
                MessageBox.alert(timeoutMsg, t("sys.api.errorTip")).then(()=>{
                    switchToLoginPage(true);
                });
                //TODO:store.dispatch("logout");
                break;
            default:
                !!message && (timeoutMsg = message);
                break;
        }

        throw new Error(timeoutMsg || t("sys.api.apiRequestFailed"));
    },

    // 请求之前处理config
    beforeRequestHook: (config, options) => {
        const { apiUrl, joinPrefix, joinParamsToUrl, formatDate, joinTime = true, urlPrefix } = options;

        // http开头的请求url，不加前缀
        let isStartWithHttp = false;
        const requestUrl = config.url;
        if (requestUrl != null && (requestUrl.startsWith("http:") || requestUrl.startsWith("https:"))) {
            isStartWithHttp = true;
        }
        if (!isStartWithHttp && joinPrefix) {
            config.url = `${urlPrefix}${config.url}`;
        }

        if (!isStartWithHttp && apiUrl && isString(apiUrl)) {
            config.url = `${apiUrl}${config.url}`;
        }

        const params = config.params || {};
        const data = config.data || false;
        formatDate && data && !isString(data) && formatRequestDate(data);
        if ((config.method || {}).toUpperCase() === RequestEnum.GET) {
            if (!isString(params)) {
                // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
                config.params = Object.assign(params || {}, joinTimestamp(joinTime, false));
            } else {
                // 兼容restful风格
                config.url = config.url + params + `${joinTimestamp(joinTime, true)}`;
                config.params = undefined;
            }
        } else {
            if (!isString(params)) {
                formatDate && formatRequestDate(params);
                if (Reflect.has(config, "data") && config.data && Object.keys(config.data).length > 0) {
                    config.data = data;
                    config.params = params;
                } else {
                    // 非GET请求如果没有提供data，则将params视为data
                    config.data = params;
                    config.params = undefined;
                }
                if (joinParamsToUrl) {
                    config.url = setObjToUrlParams(config.url, Object.assign({}, config.params, config.data));
                }
            } else {
                // 兼容restful风格
                config.url = config.url + params;
                config.params = undefined;
            }
        }
        return config;
    },

    /**
     * @description: 请求拦截器处理
     */
    requestInterceptors: (config, options) => {
        // 请求之前处理config
        const token = getToken();
        let tenantId = getTenantId();

        //check token in memory is valid
        if (!store.getters.isTokenValid) {
            currentGlobalRequestStatus = GlobalRequestStatusEnum.INVALIDATE;
        }
        if (currentGlobalRequestStatus !== GlobalRequestStatusEnum.NORMAL) {
            setGlobalMessageBoxSilent();
            showUserChooseBox();
            return Promise.reject(new Error(t("sys.api.errTokenChangedByOtherPageTip")));
        }

        // 将签名和时间戳，添加在请求接口 Header
        config.headers[ConfigEnum.TIMESTAMP] = signMd5Utils.getTimestamp();
        config.headers[ConfigEnum.Sign] = signMd5Utils.getSign(
            config.url,
            cloneDeep(config.params),
            cloneDeep(config.data)
        );
        config.headers[ConfigEnum.VERSION] = "v3";
        config.headers[ConfigEnum.SUBSYSTEM] = env.code;
        if (!!token && ((config || {}).requestOptions || {}).withToken !== false) {
            // jwt token
            config.headers.Authorization = options.authenticationScheme
                ? `${options.authenticationScheme} ${token}`
                : token;
            config.headers[ConfigEnum.TOKEN] = token;

            // 将签名和时间戳，添加在请求接口 Header
            //config.headers[ConfigEnum.TIMESTAMP] = signMd5Utils.getTimestamp();
            //config.headers[ConfigEnum.Sign] = signMd5Utils.getSign(config.url, config.params);
            if (!tenantId) tenantId = 0;
            config.headers[ConfigEnum.TENANT_ID] = tenantId;
        }
        return config;
    },

    /**
     * @description: 响应拦截器处理
     */
    responseInterceptors: (res) => {
        return res;
    },

    /**
     * @description: 响应错误处理
     */
    responseInterceptorsCatch: async (error) => {
        const { response, code, message, config } = error || {};
        const errorMessageMode = ((config || {}).requestOptions || {}).errorMessageMode || "none";
        //scott 20211022 token失效提示信息
        //const msg: string = response?.data?.error?.message ?? '';
        const msg = ((response || {}).data || {}).message || "";
        const err = !!error ? error.toString() : "";
        let errMessage = "";

        try {
            if (code === "ECONNABORTED" && message.indexOf("timeout") !== -1) {
                errMessage = t("sys.api.apiTimeoutMessage");
            }
            if ((err || {}).includes("Network Error")) {
                errMessage = t("sys.api.networkExceptionMsg");
            }

            if (errMessage) {
                return Promise.reject(new Error(errMessage));
            }
        } catch (error) {
            throw new Error(error);
        }

        const convertedMsg = await dealApiResponseError(((error || {}).response || {}).status, msg, errorMessageMode);
        return Promise.reject(!!convertedMsg ? new Error(convertedMsg) : error);
    },
};

/**
 *
 * @param {Partial<CreateAxiosOptions>} opt
 * @returns
 */
function createAxios(opt) {
    return new VAxios(
        deepMerge(
            {
                // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
                // authentication schemes，e.g: Bearer
                // authenticationScheme: 'Bearer',
                authenticationScheme: "",
                //接口超时设置
                timeout: 10 * 1000,
                // 基础接口地址
                // baseURL: globSetting.apiUrl,
                headers: { "Content-Type": ContentTypeEnum.JSON },
                // 如果是form-data格式
                // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
                // 数据处理方式
                transform,
                // 配置项，下面的选项都可以在独立的接口请求中覆盖
                requestOptions: {
                    // 默认将prefix 添加到url
                    joinPrefix: true,
                    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
                    isReturnNativeResponse: false,
                    // 需要对返回数据进行处理
                    isTransformResponse: true,
                    // post请求的时候添加参数到url
                    joinParamsToUrl: false,
                    // 格式化提交参数时间
                    formatDate: true,
                    // 异常消息提示类型
                    errorMessageMode: "none",
                    // 成功消息提示类型
                    successMessageMode: "success",
                    // 接口地址
                    apiUrl: env.apiBaseUri,
                    // 接口拼接地址
                    //  是否加入时间戳
                    joinTime: true,
                    // 忽略重复请求
                    ignoreCancelToken: true,
                    // 是否携带token
                    withToken: true,
                    urlPrefix: "",
                },
            },
            opt || {}
        )
    );
}
export const defHttp = createAxios();
